import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import * as Sentry from "@sentry/react";
import Keycloak, { KeycloakAdapter, PromiseObject } from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { AuthContext } from "./AuthContext.js";
import { keycloakCapacitorTokenStorage } from "./keycloakCapacitorTokenStorage.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthCapacitorProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    function persistTokens() {
      if (keycloak.token && keycloak.refreshToken) {
        void keycloakCapacitorTokenStorage.write({
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
          idToken: keycloak.idToken,
        });
      }
    }

    function clearTokens() {
      void keycloakCapacitorTokenStorage.clear();
    }

    async function bootstrap() {
      try {
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = () => {
          setIsAuthenticatedState();
        };
        keycloak.onAuthLogout = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshError = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onTokenExpired = () => {
          // Rotate the access token using the refresh token; the resulting
          // onAuthRefreshSuccess / onAuthRefreshError event drives state and
          // persistence. Without this, an expired access token would never be
          // refreshed and the user would be silently logged out.
          keycloak.updateToken(5).catch(e => {
            clearTokens();
            Sentry.captureException(e);
          });
        };
        keycloak.onAuthSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };

        const storedTokens = await keycloakCapacitorTokenStorage.read();

        await keycloak.init({
          // reference: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L75
          adapter: getKeycloakCapacitorAdapter(keycloak),
          checkLoginIframe: false,
          responseMode: "query",
          onLoad: "check-sso",
          // flow: "hybrid", // 'implicit', 'hybrid',
          redirectUri: "de.meistersystems:/keycloak",
          token: storedTokens?.token,
          refreshToken: storedTokens?.refreshToken,
          idToken: storedTokens?.idToken,
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          keycloak.idTokenParsed?.groups
            ? keycloak.idTokenParsed.groups.includes(groupName)
            : false,
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

async function openBrowserWithCallback(
  promise: PromiseObject<void>,
  url: string,
  onDataCallback: (data: URLOpenListenerEvent) => void | Promise<void>
) {
  const addUrlListener = await App.addListener("appUrlOpen", onDataCallback);

  promise.promise.finally(() => {
    addUrlListener.remove();
    Browser.close();
  });

  try {
    await Browser.open({ url, presentationStyle: "popover" });
  } catch (e) {
    if (e instanceof Error) {
      Sentry.withScope(scope => {
        scope.setTag("service", "Capacitor");
        scope.setExtra("method", "openBrowserWithCallback");
        scope.setExtra("url", url);
        Sentry.captureException(e);
      });
      promise.setError(e);
    }
  }
}

async function openBrowser(promise: PromiseObject<void>, url: string) {
  try {
    await Browser.open({ url, presentationStyle: "popover" });
    promise.setSuccess();
  } catch (e) {
    if (e instanceof Error) {
      Sentry.withScope(scope => {
        scope.setTag("service", "Capacitor");
        scope.setExtra("method", "openBrowser");
        scope.setExtra("url", url);
        Sentry.captureException(e);
      });
      promise.setError(e);
    }
  }
}

// references: https://github.com/edgeflare/keycloak-angular-capacitor/blob/master/projects/keycloak-angular-capacitor/src/lib/keycloak-angular-capacitor.service.ts
// taken from: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L1668
const getKeycloakCapacitorAdapter = (keycloak: Keycloak): KeycloakAdapter => ({
  login: options => {
    const promise = keycloak.createPromise<void>();
    const loginUrl = keycloak.createLoginUrl(options);

    openBrowserWithCallback(promise, loginUrl, data => {
      const oauth = keycloak.parseCallback(data.url);
      keycloak.processCallback(oauth, promise);
    });

    return promise.promise;
  },

  logout: options => {
    const promise = keycloak.createPromise<void>();
    const logoutUrl = keycloak.createLogoutUrl(options);

    openBrowserWithCallback(promise, logoutUrl, data => {
      keycloak.clearToken();
      promise.setSuccess();
    });

    return promise.promise;
  },

  register: options => {
    const promise = keycloak.createPromise<void>();
    const registerUrl = keycloak.createRegisterUrl(options);

    openBrowserWithCallback(promise, registerUrl, data => {
      const oauth = keycloak.parseCallback(data.url);
      keycloak.processCallback(oauth, promise);
    });

    return promise.promise;
  },

  accountManagement: () => {
    const promise = keycloak.createPromise<void>();
    const accountUrl = keycloak.createAccountUrl();
    if (!accountUrl) {
      throw new Error("Not supported by the OIDC server");
    }

    openBrowser(promise, accountUrl);

    return promise.promise;
  },

  redirectUri: options => {
    if (options && options.redirectUri) {
      return options.redirectUri;
    } else if (keycloak.redirectUri) {
      return keycloak.redirectUri;
    } else {
      return "http://localhost";
    }
  },
});
